import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { BusinessLicenseProps } from "../../Models/BusinessLicense";

import {
    registerEmail, checkEmailVerification, checkMobileVerification,
    registerBusiness, registerAdminUser,

    loginEmail, checkPasscodeLogin,
    cancelLogin,
    getCompanyTypes,
    onCancel,
    updateAccountType,
    getAction,
    onSkip,
    onBack,
    checkUserRegistrationStatus,
    //addressLookup,
    //addressGet,
    lookupAdvancedCompanyHouse,
    lookupCompanyHouse,
    licensePlanCalculate,
    licensePlanCreate,
    processPayment,
    getSiteOwnershipOptions,
    registerSite,
    licensePlansGet,
    sendMobileOtp,
} from './thunk';
import { getLoggedinUser, setAuthorization } from "helpers/api_helper";

const pickNumber = (...values: any[]) => {
    for (const value of values) {
        if (value === '' || value === null || value === undefined) continue;
        const num = Number(value);
        if (!Number.isNaN(num)) return num;
    }
    return undefined;
};

const pickString = (...values: any[]) => {
    for (const value of values) {
        if (typeof value === 'string' && value.trim() !== '') return value;
    }
    return undefined;
};

interface Co2AuthState {
    email: string | undefined;

    web_app_auth_method: string | undefined;
    identifier: string | undefined;

    onboarding: boolean | undefined;
    business: BusinessProps;
    user: UserProps;
    businessLicense: BusinessLicenseProps;
    currentAction?: ActionProps;
    previousAction?: ActionProps;

    termsAccepted: boolean;

    account_type?: string,
    business_group_type?: string,

    licenseSettings?: any,
    voucherIsValid: boolean,
    autoRenew: boolean,

    // Persisted selection across steps
    selectedBillingCycle?: string,
    selectedBillingCycleMonths?: number,
    selectedUsers?: number,
    selectedSites?: number,
    selectedVoucherCode?: string,

    companyHouse: any,
    companyTypes: CompanyTypeProps[],
    licensePlans: any[],
    trialPlan: { name: string; days: number; includedUsers: number; includedSites: number } | null,
    siteOwnershipOptions: any[],

    business_address?: AddressProps,
    trading_address?: AddressProps,

    loader: boolean;
    error: any;

    token: string | undefined;
    currentPage: string;
    nextPathname: string | undefined;

    showHelp: boolean;

    passcode_expire: number;
    has_fixed_code: boolean;
    can_send_sms: boolean;
    sms_identifier: string;

    can_set_business_details: boolean;
}

export const initialState: Co2AuthState = {
    email: undefined,
    web_app_auth_method: undefined,
    identifier: undefined,

    onboarding: false,
    business: {},
    user: {},
    businessLicense: {},
    termsAccepted: false,
    account_type: undefined,
    business_group_type: undefined,

    licenseSettings: undefined,
    voucherIsValid: true,
    autoRenew: true,

    selectedBillingCycle: undefined,
    selectedBillingCycleMonths: undefined,
    selectedUsers: 0,
    selectedSites: 0,
    selectedVoucherCode: undefined,

    currentAction: undefined,
    previousAction: undefined,

    //Lists
    companyHouse: undefined,
    companyTypes: [],
    licensePlans: [],
    trialPlan: null,
    siteOwnershipOptions: [],

    business_address: undefined,
    trading_address: undefined,

    loader: false,
    error: undefined,
    currentPage: "email",
    token: getLoggedinUser()?.token,
    nextPathname: undefined,

    showHelp: false,

    passcode_expire: 60,
    has_fixed_code: false,
    can_send_sms: false,
    sms_identifier: '',

    can_set_business_details: false,
};

const Co2AuthSlice = createSlice({
    name: "co2auth",
    initialState,
    reducers: {
        updateEmailAction(state: any, action: any) {
            state.email = action.payload;
        },
        updateLicenseSelectionAction(state: any, action: PayloadAction<{ billingCycle?: string; billingCycleMonths?: number; users?: number; sites?: number; voucherCode?: string }>) {
            const { billingCycle, billingCycleMonths, users, sites, voucherCode } = action.payload || {} as any;
            if (billingCycle !== undefined) state.selectedBillingCycle = billingCycle;
            if (billingCycleMonths !== undefined) state.selectedBillingCycleMonths = billingCycleMonths;
            if (users !== undefined) state.selectedUsers = users;
            if (sites !== undefined) state.selectedSites = sites;
            if (voucherCode !== undefined) state.selectedVoucherCode = voucherCode;
        },
        setSelectedLicensePlanAction(state: any, action: PayloadAction<{ planCode: string; includedUsers: number; includedSites: number; basePeriodCost: number; planMonths: number; additionalUserCost?: number; additionalSiteCost?: number; selectedPlanName?: string; }>) {
            if (!state.licenseSettings) return;
            const { planCode, includedUsers, includedSites, basePeriodCost, planMonths, additionalUserCost, additionalSiteCost, selectedPlanName } = action.payload || ({} as any);
            const months = planMonths === 12 ? 12 : 1;
            state.licenseSettings = {
                ...state.licenseSettings,
                selectedPlanCode: planCode,
                selectedPlanName: selectedPlanName ?? state.licenseSettings.selectedPlanName,
                plan_months: planMonths ?? state.licenseSettings.plan_months,
                selectedPlanBaseCost: basePeriodCost ?? state.licenseSettings.selectedPlanBaseCost,
                selectedPlanAdditionalUserCostPerMonth: (additionalUserCost != null ? Number(additionalUserCost) : undefined) != null ? Number(additionalUserCost) / months : state.licenseSettings.selectedPlanAdditionalUserCostPerMonth,
                selectedPlanAdditionalSiteCostPerMonth: (additionalSiteCost != null ? Number(additionalSiteCost) : undefined) != null ? Number(additionalSiteCost) / months : state.licenseSettings.selectedPlanAdditionalSiteCostPerMonth,
                defaults: {
                    ...state.licenseSettings.defaults,
                    allowedUsers: includedUsers != null ? Number(includedUsers) : state.licenseSettings.defaults?.allowedUsers,
                    allowedSites: includedSites != null ? Number(includedSites) : state.licenseSettings.defaults?.allowedSites,
                }
            };
        },
        clearSelectedLicensePlanAction(state: any) {
            if (!state.licenseSettings) return;
            state.licenseSettings = {
                ...state.licenseSettings,
                selectedPlanCode: undefined,
                selectedPlanBaseCost: undefined,
                selectedPlanAdditionalUserCostPerMonth: undefined,
                selectedPlanAdditionalSiteCostPerMonth: undefined,
            };
        },
        updateBusinessAction(state: any, action: any) {
            state.business = action.payload;
        },
        updateUserAction(state: any, action: any) {
            state.user = action.payload;
        },
        updateBusinessLicenseAction(state: any, action: any) {
            state.businessLicense = action.payload;
        },
        updateTandCAction(state: any, action: any) {
            state.termsAccepted = action.payload;
        },
        updateShowHelpAction(state: any, action: any) {
            state.showHelp = action.payload;
        },
        updateAddressesAction(state: any, action: any) {
            state.business_address = action.payload.business_address;
            state.trading_address = action.payload.trading_address;
        },
        updateAutoRenewAction(state: any, action: any) {
            state.autoRenew = action.payload;
        },
        clearCompanyHouseResultsAction(state: any) {
            state.companyHouse = undefined;
        },
        clearVoucherIsValidAction(state: any) {
            state.voucherIsValid = true;
        },
        setLoaderAction(state: any) {
            state.loader = true;
            state.error = undefined;
        },
        setCurrentPageAction(state: any, action: any) {
            state.currentPage = action.payload;
            state.error = undefined;
            if (action.payload !== 'otp') {
                state.has_fixed_code = false;
            }
        },
        setErrorAction(state: any, action: any) {
            state.error = action.payload;
        },
        setUserAction(state: any) {
            var data = {
                token: state.token,
                user: {
                    first_name: state.user.first_name,
                    last_name: state.user.last_name,
                    email: state.email,
                    id: state.user.id,
                }
            }
            sessionStorage.setItem("authUser", JSON.stringify(data));
            //sessionStorage.setItem('homePage', '/profile');
            state.currentPage = "home";
        },
        clearBusinessDetailsAction(state: any) {
            state.business.business_name = undefined;
            state.business.trading_name = undefined;
            state.business.company_type = undefined;
            state.business.company_no = undefined;
            state.business.sicc_codes = undefined;
            state.business_address = undefined;
            state.trading_address = undefined;
        },
        resetRegistrationFlowAction(state: any) {
            const showHelp = state.showHelp;
            const nextPathname = state.nextPathname;
            Object.assign(state, {
                ...initialState,
                showHelp,
                nextPathname,
            });
        },
        setAccountTypeAction(state: any, action: any) {
            state.account_type = action.payload.account_type;
            state.business_group_type = action.payload.business_group_type;
        },
        setNextPathnameAction(state: any, action: any) {
            state.nextPathname = action.payload;
        },
    },
    extraReducers: (builder) => {
        //Register Email
        builder.addCase(registerEmail.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentPage = action.payload.next_action.process_action;//"verify_email";

            state.currentAction = action.payload.next_action;
            state.identifier = action.payload.identifier ?? state.email;
            state.passcode_expire = action.payload.passcode_expire;
        });
        builder.addCase(registerEmail.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Login Email
        builder.addCase(loginEmail.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            if (action.payload.onboarding) {
                state.currentPage = "verify_email";
                state.account_type = action.payload.account_type;
                state.onboarding = action.payload.onboarding;
            }
            else
                state.currentPage = "otp";
            state.currentAction = action.payload.next_action;
            state.web_app_auth_method = action.payload.web_app_auth_method;
            state.identifier = action.payload.identifier;
            state.passcode_expire = action.payload.passcode_expire;
            state.has_fixed_code = action.payload?.has_fixed_code === true;
            state.can_send_sms = action.payload.can_send_sms;
            state.sms_identifier = action.payload.sms_identifier;
        });
        builder.addCase(loginEmail.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Send Mobile OTP (resend)
        builder.addCase(sendMobileOtp.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            // keep currentPage as is (usually 'otp')
            state.web_app_auth_method = action.payload.web_app_auth_method;
            state.identifier = action.payload.identifier;
            state.passcode_expire = action.payload.passcode_expire;
            state.has_fixed_code = action.payload?.has_fixed_code === true;
            state.can_send_sms = action.payload.can_send_sms ?? state.can_send_sms;
            state.sms_identifier = action.payload.sms_identifier ?? state.sms_identifier;
        });
        builder.addCase(sendMobileOtp.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Check Register Email Verification
        builder.addCase(checkEmailVerification.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;

            state.token = action.payload.token;

            setAuthorization(action.payload.token);
            sessionStorage.setItem("authUser", JSON.stringify(action.payload));
            if (action.payload.default_dashboard)
                sessionStorage.setItem('homePage', action.payload.default_dashboard);

            state.user = action.payload.user;
            state.business = action.payload.business;
            state.termsAccepted = action.payload.accepted_t_and_c;
            state.businessLicense = action.payload.business_license;
            state.account_type = action.payload.account_type || "none";
            state.business_group_type = action.payload.business_group_type || "none";
            state.business_address = action.payload.business_address;
            const tradingPayload = action.payload.trading_address;
            const trading =
                Array.isArray(tradingPayload) && tradingPayload.length > 0
                    ? tradingPayload[0]
                    : tradingPayload || null;
            state.trading_address = trading || state.business_address;

            // Restore license settings from the API response if available
            const license = action.payload.license;
            if (license) {
                const licenseData = license.license_data;
                const calculations = license.calculations;
                const persistedPlanName = pickString(
                    licenseData?.selectedPlanName,
                    licenseData?.planName,
                    license?.plan_name,
                    license?.license_plan_name
                );
                const persistedPlanMonths = pickNumber(
                    licenseData?.plan_months,
                    license?.plan_months,
                    calculations?.plan_months
                );
                const restoredBillingCycle = pickString(
                    licenseData?.billingCycle,
                    licenseData?.billing_cycle,
                    calculations?.billingCycle,
                    calculations?.billing_cycle,
                    persistedPlanMonths === 12 ? 'annual' : undefined
                );
                const restoredUsers = pickNumber(
                    licenseData?.users,
                    licenseData?.additional_users,
                    calculations?.users,
                    calculations?.additional_users
                );
                const restoredSites = pickNumber(
                    licenseData?.sites,
                    licenseData?.additional_sites,
                    calculations?.sites,
                    calculations?.additional_sites
                );
                const restoredPlanCode = pickString(
                    licenseData?.selectedPlanCode,
                    licenseData?.selected_plan_code,
                    licenseData?.planCode,
                    licenseData?.license_plan_id != null ? String(licenseData.license_plan_id) : undefined,
                    license?.license_plan_id != null ? String(license.license_plan_id) : undefined
                );
                const restoredMonths = restoredBillingCycle === 'annual' ? 12 : 1;
                const calcUsersMonthly = pickNumber(
                    calculations?.additionalUsersCostExclVat,
                    calculations?.additional_users_cost_excl_vat,
                );
                const calcSitesMonthly = pickNumber(
                    calculations?.additionalSitesCostExclVat,
                    calculations?.additional_sites_cost_excl_vat,
                );
                const calcUsersPeriod = calcUsersMonthly != null ? Number(calcUsersMonthly) * restoredMonths : 0;
                const calcSitesPeriod = calcSitesMonthly != null ? Number(calcSitesMonthly) * restoredMonths : 0;
                const calcTotalExcl = pickNumber(
                    calculations?.totalExclVat,
                    calculations?.total_excl_vat,
                );
                const calcBaseFromTotals = calcTotalExcl != null
                    ? Math.max(0, Number(calcTotalExcl) - calcUsersPeriod - calcSitesPeriod)
                    : undefined;
                const calcAdditionalUserPerMonth = (calcUsersMonthly != null && Number(restoredUsers || 0) > 0)
                    ? Number(calcUsersMonthly) / Number(restoredUsers || 1)
                    : undefined;
                const calcAdditionalSitePerMonth = (calcSitesMonthly != null && Number(restoredSites || 0) > 0)
                    ? Number(calcSitesMonthly) / Number(restoredSites || 1)
                    : undefined;
                const persistedBaseCost = pickNumber(
                    licenseData?.selectedPlanBaseCost,
                    licenseData?.basePeriodCost,
                    licenseData?.base_period_cost,
                    calculations?.selectedPlanBaseCost,
                    calculations?.basePeriodCost,
                    calculations?.base_period_cost,
                    calcBaseFromTotals,
                    license?.base_period_license_cost,
                    license?.base_cost,
                    license?.base_license_cost,
                );
                const persistedAdditionalUser = pickNumber(
                    licenseData?.additionalUserCostPerMonth,
                    licenseData?.additional_user_cost_per_month,
                    calculations?.additionalUserCostPerMonth,
                    calculations?.additional_user_cost_per_month,
                    calcAdditionalUserPerMonth,
                    license?.additional_user_cost_per_month,
                    license?.additional_user_cost,
                );
                const persistedAdditionalSite = pickNumber(
                    licenseData?.additionalSiteCostPerMonth,
                    licenseData?.additional_site_cost_per_month,
                    calculations?.additionalSiteCostPerMonth,
                    calculations?.additional_site_cost_per_month,
                    calcAdditionalSitePerMonth,
                    license?.additional_site_cost_per_month,
                    license?.additional_site_cost,
                );
                
                if (licenseData) {
                    // Restore selection values from the saved license
                    // Backend now returns users/sites as additional (not total)
                    state.selectedBillingCycle = restoredBillingCycle || state.selectedBillingCycle;
                    if (restoredBillingCycle) {
                        state.selectedBillingCycleMonths = restoredBillingCycle === 'annual' ? 12 : 1;
                    }
                    state.selectedUsers = restoredUsers != null ? Number(restoredUsers) : state.selectedUsers;
                    state.selectedSites = restoredSites != null ? Number(restoredSites) : state.selectedSites;
                    state.selectedVoucherCode = pickString(
                        licenseData?.voucherCode,
                        licenseData?.voucher_code,
                        license?.voucher_code,
                        license?.voucher?.voucher_code
                    );
                    state.selectedPlanCode = restoredPlanCode || state.selectedPlanCode;
                    
                    // Ensure selectedUsers and selectedSites are not negative
                    if (state.selectedUsers < 0) state.selectedUsers = 0;
                    if (state.selectedSites < 0) state.selectedSites = 0;
                }
                
                if (calculations) {
                    // Restore license settings/calculations with plan defaults
                    const includedUsers = pickNumber(
                        licenseData?.includedUsers,
                        licenseData?.included_users,
                        calculations?.defaults?.allowedUsers,
                        calculations?.defaults?.allowed_users,
                    ) ?? 1;
                    const includedSites = pickNumber(
                        licenseData?.includedSites,
                        licenseData?.included_sites,
                        calculations?.defaults?.allowedSites,
                        calculations?.defaults?.allowed_sites,
                    ) ?? 1;
                    
                    state.licenseSettings = {
                        ...calculations,
                        billingCycle: restoredBillingCycle || calculations.billingCycle || calculations.billing_cycle,
                        selectedPlanCode: restoredPlanCode || state.licenseSettings?.selectedPlanCode,
                        selectedPlanName: persistedPlanName ?? state.licenseSettings?.selectedPlanName,
                        plan_months: persistedPlanMonths ?? state.licenseSettings?.plan_months,
                        selectedPlanBaseCost: persistedBaseCost ?? state.licenseSettings?.selectedPlanBaseCost,
                        selectedPlanAdditionalUserCostPerMonth: persistedAdditionalUser ?? state.licenseSettings?.selectedPlanAdditionalUserCostPerMonth,
                        selectedPlanAdditionalSiteCostPerMonth: persistedAdditionalSite ?? state.licenseSettings?.selectedPlanAdditionalSiteCostPerMonth,
                        defaults: {
                            ...(calculations.defaults || {}),
                            allowedUsers: includedUsers,
                            allowedSites: includedSites,
                        },
                    };
                }
            }
            if (action.payload.next_action == null) {
                if (action.payload.token) {
                    state.currentPage = "home";
                    state.currentAction = null;
                } else if (action.payload.reg_complete && action.payload.reg_complete.business === true
                    && action.payload.reg_complete.license === true
                    && action.payload.reg_complete.user === true
                ) {
                    state.currentPage = "home";
                    state.currentAction = null;
                } else {
                    //state.currentPage = "email";
                    state.loader = false;
                    state.error = "An error occured. Please try again.";
                }
            } else {
                state.currentPage = action.payload.next_action.process_action;
                state.currentAction = action.payload.next_action;
            }
        });
        builder.addCase(checkEmailVerification.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Check Register Mobile Verification
        builder.addCase(checkMobileVerification.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentPage = action.payload.next_action.process_action;
            state.currentAction = action.payload.next_action;
            state.passcode_expire = action.payload.passcode_expire;
        });
        builder.addCase(checkMobileVerification.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Check Login Email Verification
        builder.addCase(checkPasscodeLogin.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            sessionStorage.setItem("authUser", JSON.stringify(action.payload));
            if (action.payload.default_dashboard)
                sessionStorage.setItem('homePage', action.payload.default_dashboard);
            state.token = action.payload.token;
            setAuthorization(action.payload.token);
            state.onboarding = false; // Reset onboarding flag after successful login
            state.has_fixed_code = false;
            state.currentPage = "home";
        });
        builder.addCase(checkPasscodeLogin.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        builder.addCase(cancelLogin.fulfilled, (state: any) => {
            state.loader = false;
            state.error = undefined;
        });
        builder.addCase(cancelLogin.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Account Type
        builder.addCase(updateAccountType.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.can_set_business_details = true;
            if (action.payload.default_dashboard)
                sessionStorage.setItem('homePage', action.payload.default_dashboard);
            //state.currentPage = action.payload.next_action.process_action;
            //state.currentAction = action.payload.next_action;
        });
        builder.addCase(updateAccountType.rejected, (state: any, action: any) => {
            state.can_set_business_details = false;
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Register Business
        builder.addCase(registerBusiness.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            // Step 5 must use the business exactly as persisted by the Step 3
            // request. Previously this response was ignored, leaving a stale
            // business object (often with no ISO country) for PhonePicker.
            if (action.payload?.business) {
                state.business = action.payload.business;
            }
            state.currentPage = action.payload.next_action.process_action;
            state.currentAction = action.payload.next_action;
            //state.business_address.id = action.payload.business_address.id;
            //state.trading_address.id = action.payload.trading_address.id;
        });
        builder.addCase(registerBusiness.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Lookup Advanced Company House
        builder.addCase(lookupAdvancedCompanyHouse.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.companyHouse = action.payload;
        });
        builder.addCase(lookupAdvancedCompanyHouse.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            state.companyHouse = [];
        });

        //Lookup Company House
        builder.addCase(lookupCompanyHouse.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.companyHouse = action.payload;
        });
        builder.addCase(lookupCompanyHouse.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            state.companyHouse = [];
        });

        //Get Company Types
        builder.addCase(getCompanyTypes.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.companyTypes = action.payload.company_types;
        });
        builder.addCase(getCompanyTypes.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            state.companyTypes = [];
        });

        //Register Site
        builder.addCase(registerSite.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentPage = action.payload.next_action.process_action;
            state.currentAction = action.payload.next_action;
            if (action.payload.business_address) {
                state.business_address = action.payload.business_address;
            }
            if (action.payload.trading_address) {
                const trading =
                    Array.isArray(action.payload.trading_address) && action.payload.trading_address.length > 0
                        ? action.payload.trading_address[0]
                        : action.payload.trading_address;
                state.trading_address = trading;
            }
            if (action.payload.default_dashboard)
                sessionStorage.setItem('homePage', action.payload.default_dashboard);
        });
        builder.addCase(registerSite.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Get Site Ownership Options
        builder.addCase(getSiteOwnershipOptions.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.siteOwnershipOptions = action.payload.statuses;
        });
        builder.addCase(getSiteOwnershipOptions.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            state.siteOwnershipOptions = [];
        });

        //Register Admin User
        builder.addCase(registerAdminUser.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.user = action.payload.user;
            state.currentPage = action.payload.next_action.process_action;
            state.currentAction = action.payload.next_action;
            state.previousAction = action.payload.prev_action;
            state.passcode_expire = action.payload.passcode_expire;
        });
        builder.addCase(registerAdminUser.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Initial License Settings removed; licensePlanCalculate will populate settings

        //License Plan Calculate
        builder.addCase(licensePlanCalculate.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            const prev = state.licenseSettings || {};
            state.licenseSettings = {
                ...action.payload,
                defaults: (prev.defaults || action.payload.defaults || {}),
                selectedPlanCode: prev.selectedPlanCode,
                selectedPlanName: prev.selectedPlanName ?? action.payload.selectedPlanName,
                plan_months: prev.plan_months ?? action.payload.plan_months,
                selectedPlanBaseCost: prev.selectedPlanBaseCost,
                selectedPlanAdditionalUserCostPerMonth: prev.selectedPlanAdditionalUserCostPerMonth,
                selectedPlanAdditionalSiteCostPerMonth: prev.selectedPlanAdditionalSiteCostPerMonth,
            };
            state.voucherIsValid = !action.payload.voucherInvalid;
            // Store voucher code in state for use during payment only when valid
            const voucherCode = action.meta?.arg?.voucherCode || action.payload?.voucher?.voucher_code;
            if (!action.payload.voucherInvalid && voucherCode) {
                state.selectedVoucherCode = voucherCode;
            } else if (action.payload.voucherInvalid) {
                state.selectedVoucherCode = '';
            }
        });
        builder.addCase(licensePlanCalculate.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            //if (action.error.message === "Request failed with status code 422") {
            //state.voucherIsValid = false;
            //state.error = "";
            //}
        });

        //License Plan Create
        builder.addCase(licensePlanCreate.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentPage = action.payload.next_action.process_action;
            state.currentAction = action.payload.next_action;
            state.voucherIsValid = !action.payload.voucherInvalid;

            // Store voucher code from the action input for use during payment step
            const voucherCode = action.meta?.arg?.voucherCode;
            if (voucherCode) {
                state.selectedVoucherCode = voucherCode;
            }

            // Merge calculations from API response into licenseSettings to preserve pricing on Step 8
            const results = action.payload.results || {};
            const calculations =
                results.calculations ||
                results.license?.calculations ||
                results.licenses?.calculations ||
                action.payload.license?.calculations ||
                {};

            if (state.licenseSettings) {
                // Update licenseSettings with calculations from the API response
                state.licenseSettings = {
                    ...state.licenseSettings,
                    ...calculations,
                    // Preserve plan selection fields from existing state
                    selectedPlanCode: state.licenseSettings.selectedPlanCode,
                    selectedPlanName: state.licenseSettings.selectedPlanName,
                    plan_months: state.licenseSettings.plan_months,
                    // Keep selected plan/unit prices from the chosen plan.
                    // license.* values may include computed totals for the pending license
                    // and can collapse users/sites rows when navigating back from payment.
                    selectedPlanBaseCost: state.licenseSettings.selectedPlanBaseCost,
                    selectedPlanAdditionalUserCostPerMonth: state.licenseSettings.selectedPlanAdditionalUserCostPerMonth,
                    selectedPlanAdditionalSiteCostPerMonth: state.licenseSettings.selectedPlanAdditionalSiteCostPerMonth,
                    // Preserve defaults
                    defaults: state.licenseSettings.defaults,
                };
            }
        });
        builder.addCase(licensePlanCreate.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Process Payment
        builder.addCase(processPayment.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentPage = action.payload.next_action.process_action;
            state.currentAction = action.payload.next_action;
            if (action.payload.default_dashboard)
                sessionStorage.setItem('homePage', action.payload.default_dashboard);
        });
        builder.addCase(processPayment.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Get Action
        builder.addCase(getAction.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentPage = action.payload.next_action.process_action;
            state.currentAction = action.payload.next_action;
        });
        builder.addCase(getAction.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //On Cancel
        builder.addCase(onCancel.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentAction = action.payload.next_action;
        });
        builder.addCase(onCancel.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //On Skip
        builder.addCase(onSkip.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentPage = action.payload.next_action.process_action;
            state.currentAction = action.payload.next_action;
        });
        builder.addCase(onSkip.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //On Back
        builder.addCase(onBack.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.currentPage = action.payload.process_action || "email";
            state.currentAction = action.payload;
        });
        builder.addCase(onBack.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        //Check User Registration Status
        builder.addCase(checkUserRegistrationStatus.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;

            if (action.payload.default_dashboard)
                sessionStorage.setItem('homePage', action.payload.default_dashboard);

            state.user = action.payload.user;
            state.business = action.payload.business;
            state.termsAccepted = action.payload.accepted_t_and_c;
            state.businessLicense = action.payload.business_license;
            state.account_type = action.payload.account_type || "none";
            state.business_group_type = action.payload.business_group_type || "none";
            state.business_address = action.payload.business_address;
            if (state.account_type != 'note')
                state.can_set_business_details = true;
            if (action.payload.trading_address && action.payload.trading_address.length > 0) {
                state.trading_address = action.payload.trading_address[0];
            } else {
                state.trading_address = state.business_address;
            }

            // Restore license settings from the API response if available
            const license = action.payload.license;
            if (license) {
                const licenseData = license.license_data;
                const calculations = license.calculations;
                const persistedPlanName = pickString(
                    licenseData?.selectedPlanName,
                    licenseData?.planName,
                    license?.plan_name,
                    license?.license_plan_name
                );
                const persistedPlanMonths = pickNumber(
                    licenseData?.plan_months,
                    license?.plan_months,
                    calculations?.plan_months
                );
                const restoredBillingCycle = pickString(
                    licenseData?.billingCycle,
                    licenseData?.billing_cycle,
                    calculations?.billingCycle,
                    calculations?.billing_cycle,
                    persistedPlanMonths === 12 ? 'annual' : undefined
                );
                const restoredUsers = pickNumber(
                    licenseData?.users,
                    licenseData?.additional_users,
                    calculations?.users,
                    calculations?.additional_users
                );
                const restoredSites = pickNumber(
                    licenseData?.sites,
                    licenseData?.additional_sites,
                    calculations?.sites,
                    calculations?.additional_sites
                );
                const restoredPlanCode = pickString(
                    licenseData?.selectedPlanCode,
                    licenseData?.selected_plan_code,
                    licenseData?.planCode,
                    licenseData?.license_plan_id != null ? String(licenseData.license_plan_id) : undefined,
                    license?.license_plan_id != null ? String(license.license_plan_id) : undefined
                );
                const restoredMonths = restoredBillingCycle === 'annual' ? 12 : 1;
                const calcUsersMonthly = pickNumber(
                    calculations?.additionalUsersCostExclVat,
                    calculations?.additional_users_cost_excl_vat,
                );
                const calcSitesMonthly = pickNumber(
                    calculations?.additionalSitesCostExclVat,
                    calculations?.additional_sites_cost_excl_vat,
                );
                const calcUsersPeriod = calcUsersMonthly != null ? Number(calcUsersMonthly) * restoredMonths : 0;
                const calcSitesPeriod = calcSitesMonthly != null ? Number(calcSitesMonthly) * restoredMonths : 0;
                const calcTotalExcl = pickNumber(
                    calculations?.totalExclVat,
                    calculations?.total_excl_vat,
                );
                const calcBaseFromTotals = calcTotalExcl != null
                    ? Math.max(0, Number(calcTotalExcl) - calcUsersPeriod - calcSitesPeriod)
                    : undefined;
                const calcAdditionalUserPerMonth = (calcUsersMonthly != null && Number(restoredUsers || 0) > 0)
                    ? Number(calcUsersMonthly) / Number(restoredUsers || 1)
                    : undefined;
                const calcAdditionalSitePerMonth = (calcSitesMonthly != null && Number(restoredSites || 0) > 0)
                    ? Number(calcSitesMonthly) / Number(restoredSites || 1)
                    : undefined;
                const persistedBaseCost = pickNumber(
                    licenseData?.selectedPlanBaseCost,
                    licenseData?.basePeriodCost,
                    licenseData?.base_period_cost,
                    calculations?.selectedPlanBaseCost,
                    calculations?.basePeriodCost,
                    calculations?.base_period_cost,
                    calcBaseFromTotals,
                    license?.base_period_license_cost,
                    license?.base_cost,
                    license?.base_license_cost,
                );
                const persistedAdditionalUser = pickNumber(
                    licenseData?.additionalUserCostPerMonth,
                    licenseData?.additional_user_cost_per_month,
                    calculations?.additionalUserCostPerMonth,
                    calculations?.additional_user_cost_per_month,
                    calcAdditionalUserPerMonth,
                    license?.additional_user_cost_per_month,
                    license?.additional_user_cost,
                );
                const persistedAdditionalSite = pickNumber(
                    licenseData?.additionalSiteCostPerMonth,
                    licenseData?.additional_site_cost_per_month,
                    calculations?.additionalSiteCostPerMonth,
                    calculations?.additional_site_cost_per_month,
                    calcAdditionalSitePerMonth,
                    license?.additional_site_cost_per_month,
                    license?.additional_site_cost,
                );
                
                if (licenseData) {
                    // Restore selection values from the saved license
                    // Backend now returns users/sites as additional (not total)
                    state.selectedBillingCycle = restoredBillingCycle || state.selectedBillingCycle;
                    if (restoredBillingCycle) {
                        state.selectedBillingCycleMonths = restoredBillingCycle === 'annual' ? 12 : 1;
                    }
                    state.selectedUsers = restoredUsers != null ? Number(restoredUsers) : state.selectedUsers;
                    state.selectedSites = restoredSites != null ? Number(restoredSites) : state.selectedSites;
                    state.selectedVoucherCode = pickString(
                        licenseData?.voucherCode,
                        licenseData?.voucher_code,
                        license?.voucher_code,
                        license?.voucher?.voucher_code
                    );
                    state.selectedPlanCode = restoredPlanCode || state.selectedPlanCode;
                    
                    // Ensure selectedUsers and selectedSites are not negative
                    if (state.selectedUsers < 0) state.selectedUsers = 0;
                    if (state.selectedSites < 0) state.selectedSites = 0;
                }
                
                if (calculations) {
                    // Restore license settings/calculations with plan defaults
                    const includedUsers = pickNumber(
                        licenseData?.includedUsers,
                        licenseData?.included_users,
                        calculations?.defaults?.allowedUsers,
                        calculations?.defaults?.allowed_users,
                    ) ?? 1;
                    const includedSites = pickNumber(
                        licenseData?.includedSites,
                        licenseData?.included_sites,
                        calculations?.defaults?.allowedSites,
                        calculations?.defaults?.allowed_sites,
                    ) ?? 1;
                    
                    state.licenseSettings = {
                        ...calculations,
                        billingCycle: restoredBillingCycle || calculations.billingCycle || calculations.billing_cycle,
                        selectedPlanCode: restoredPlanCode || state.licenseSettings?.selectedPlanCode,
                        selectedPlanName: persistedPlanName ?? state.licenseSettings?.selectedPlanName,
                        plan_months: persistedPlanMonths ?? state.licenseSettings?.plan_months,
                        selectedPlanBaseCost: persistedBaseCost ?? state.licenseSettings?.selectedPlanBaseCost,
                        selectedPlanAdditionalUserCostPerMonth: persistedAdditionalUser ?? state.licenseSettings?.selectedPlanAdditionalUserCostPerMonth,
                        selectedPlanAdditionalSiteCostPerMonth: persistedAdditionalSite ?? state.licenseSettings?.selectedPlanAdditionalSiteCostPerMonth,
                        defaults: {
                            ...(calculations.defaults || {}),
                            allowedUsers: includedUsers,
                            allowedSites: includedSites,
                        },
                    };
                }
            }
            state.currentPage = action.payload.next_action ? action.payload.next_action.process_action : "home";
            state.currentAction = action.payload.next_action;
        });
        builder.addCase(checkUserRegistrationStatus.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
        });

        // License Plans Get
        builder.addCase(licensePlansGet.fulfilled, (state: any, action: any) => {
            state.loader = false;
            state.error = undefined;
            state.licensePlans = action.payload?.plans ?? action.payload ?? [];
            state.trialPlan = action.payload?.trialPlan ?? null;
        });
        builder.addCase(licensePlansGet.rejected, (state: any, action: any) => {
            state.loader = false;
            state.error = action.error.message || "An error occured. Please try again.";
            state.licensePlans = [];
            state.trialPlan = null;
        });
    },
});

export const {
    updateEmailAction, updateBusinessAction, updateUserAction, setLoaderAction, setCurrentPageAction, setErrorAction, setUserAction, setNextPathnameAction,
    updateBusinessLicenseAction, updateTandCAction, setAccountTypeAction, updateAddressesAction, clearCompanyHouseResultsAction,
    clearBusinessDetailsAction, clearVoucherIsValidAction, updateAutoRenewAction, updateShowHelpAction, resetRegistrationFlowAction,
    setSelectedLicensePlanAction, clearSelectedLicensePlanAction, updateLicenseSelectionAction,
} = Co2AuthSlice.actions;

export default Co2AuthSlice.reducer;
